<template>
  <div>
    <v-row>
      <v-col cols="5" class="ml-4">
        <v-text-field
          v-model="checklist.name"
          :label="$t('components.checklists.checklistAddEdit.checklistTitle')"
          :disabled="disabled"
          color="primary"
        ></v-text-field>
      </v-col>
      <v-col cols="5">
        <keep-alive>
          <checklist-boat-selector
            :checklist="checklist"
            @new:boat="handleNewBoat"
            :disabled="disabled || boatDisabled"
            :key="checklistIndex"
            v-if="showBoat"
          ></checklist-boat-selector>
        </keep-alive>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="1" class="mr-2">
        <checklist-toolbar
          @action="handleToolbarAction"
          :preview="!disabled && !toolbarDisabled"
          ref="checklistToolbar"
        ></checklist-toolbar>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="10" class="ml-4">
        <checklist-boat-item-selector
          :checklist="checklist"
          :disabled="true"
          v-if="showItem"
        ></checklist-boat-item-selector>
      </v-col>
    </v-row>

    <div v-if="showServicingPartner && !sameServicingPartner.active">
      <v-row no-gutters>
        <v-col cols="10" class="ml-4">
          <task-dealer-selector
            :label="
              $t('components.checklists.checklistAddEdit.servicingPartner')
            "
            :dealer-uuid.sync="checklist.crm_partner_uuid"
            :initial-dealer="checklist.crm_partner"
            :select-other="selectOther"
            :select-first="true"
            @change="handleDealerSelected"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="dealerSelected !== 'other' && dealerSelected !== null"
        no-gutters
      >
        <v-col cols="10" class="ml-4">
          <task-assignee-selector
            :initial-data="checklist.assignee"
            :professional="checklist.crm_partner_uuid"
            @change="handleAssigneeSelected"
            :key="updateAssigneeList"
          ></task-assignee-selector>
        </v-col>
      </v-row>
      <v-row v-if="dealerSelected === 'other'" no-gutters>
        <v-col cols="10" class="ml-4">
          <v-text-field
            class="mt-0 pt-0"
            v-model="checklist.unrecognizedAssigneeEmail"
            color="primary"
            :label="
              $t('components.checklists.checklistAddEdit.enterEMailAddress')
            "
            autocomplete="off"
          />
        </v-col>
      </v-row>
    </div>

    <v-form class="mb-8 ml-4 mt-4">
      <v-row no-gutters v-for="(subtask, ind) in checklist.subtasks" :key="ind">
        <v-col cols="9">
          <v-row class="pl-3">
            <v-checkbox
              disabled
              v-model="subtask.completed"
              color="primary"
              @change="saveSubtask('checkbox', subtask)"
            ></v-checkbox>
            <v-text-field
              v-model="subtask.description"
              :disabled="disabled"
            ></v-text-field>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="1" class="pt-2 mr-4">
          <v-card-actions>
            <v-btn :disabled="disabled" icon @click="deleteSubtask(ind)">
              <v-icon color="red">mdi-delete</v-icon>
            </v-btn>
          </v-card-actions>
        </v-col>
        <v-row no-gutters>
          <v-col cols="9" class="ml-8">
            <v-textarea
              class="pt-0 mt-0 pb-0 mb-0"
              color="primary"
              v-if="subtask.active"
              v-model="subtask.comment"
              rows="1"
              :label="$t('components.checklists.checklistAddEdit.comment')"
              auto-grow
              @change="saveSubtask('comment', subtask)"
              :disabled="disabled"
            >
            </v-textarea>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters class="mb-4 mr-10">
        <v-col cols="11">
          <v-textarea
            v-model="subtaskText"
            :label="
              $t(
                'components.checklists.checklistAddEdit.enterNewTaskDescription'
              )
            "
            rows="1"
            value=""
            auto-grow
            color="primary"
            @input="makeSubtask($event)"
            @blur="newSubtask"
            @keypress.enter="newSubtask"
            :disabled="disabled"
            prepend-icon="mdi-checkbox-blank-outline"
          ></v-textarea>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import ChecklistBoatSelector from "@/components/checklists/ChecklistBoatSelector";
import ChecklistToolbar from "@/components/checklists/ChecklistToolbar.vue";
import TaskDealerSelector from "@/components/selectors/TaskDealerSelector";
import TaskAssigneeSelector from "@/components/selectors/TaskAssigneeSelector";
import ChecklistBoatItemSelector from "@/components/checklists/ChecklistBoatItemSelector";

export default {
  components: {
    ChecklistToolbar,
    ChecklistBoatSelector,
    TaskDealerSelector,
    TaskAssigneeSelector,
    ChecklistBoatItemSelector,
  },
  props: {
    servicingPartnerEnabled: {
      type: Boolean,
      default: Boolean,
    },
    checklist: {
      type: Object,
      default: Object,
    },
    checklistIndex: {
      type: Number,
      default: Number,
    },
    disabled: {
      type: Boolean,
      default: true,
    },
    showBoat: {
      type: Boolean,
      default: true,
    },
    professional: {
      type: String,
      default: String,
    },
    boatDisabled: {
      type: Boolean,
      default: Boolean,
    },
    toolbarDisabled: {
      type: Boolean,
      default: Boolean,
    },
    showServicingPartner: {
      type: Boolean,
      default: true,
    },
    sameServicingPartner: {
      type: Object,
      default: Object,
    },
  },
  data: () => ({
    subtaskText: [],
    dealerSelected: null,
    selectOther: false,
    updateAssigneeList: 0,
  }),
  computed: {
    showItem() {
      return !!this.checklist.crm_boat_item;
    },
  },
  created() {
    if (
      this.$route.name === "tasksEdit" &&
      typeof this.checklist.assignee !== "undefined" &&
      this.checklist.assignee.is_external
    ) {
      this.selectOther = true;
      this.dealerSelected = "other";
      this.checklist.crm_partner = {};
      this.checklist.unrecognizedAssigneeEmail = this.checklist.assignee.email;
    }
  },
  methods: {
    handleHideChangeServicingPartner() {
      this.$refs.checklistToolbar.hideChangeServicingPartner();
    },
    handleDealerSelected(dealer) {
      if (dealer === null) {
        this.$emit("update:checklist", {
          checklist: {
            ...this.checklist,
            assignee_email: "",
            crm_partner: {},
            assignee: {},
            crm_partner_uuid: null,
            updateAssigneeList: 0,
            unrecognizedAssigneeEmail: "",
          },
          index: this.checklistIndex,
        });
        this.dealerSelected = null;
        return;
      }
      if (dealer.uuid === "other") {
        this.checklist.assignee_email = null;
        this.checklist.assignee = { ...this.checklist.assignee, delete: true };
      }
      this.checklist.crm_partner = dealer;
      this.dealerSelected = dealer.uuid;
      this.updateAssigneeList++;
      this.$emit("update:checklist", {
        checklist: this.checklist,
        index: this.checklistIndex,
      });
    },
    handleToolbarAction(event) {
      if (event.action === "remove") {
        this.$emit("delete:checklist", {
          checklist: this.checklist,
          index: this.checklistIndex,
        });
      }
      if (event.action === "change") {
        this.$emit("update:sameServicingPartner", {
          active: false,
        });
      }
    },
    deleteSubtask(index) {
      this.checklist.subtasks.splice(index, 1);
      this.$emit("update:checklist", {
        checklist: this.checklist,
        index: this.checklistIndex,
      });
    },
    makeSubtask(e) {
      let splitedText = [];
      splitedText = e.split(/\r\n|\r|\n|,/);
      this.subtaskText = splitedText.filter((elem) => elem !== "");
    },
    subtaskTemplate(description) {
      return {
        completed: null,
        comment: "",
        description: description,
      };
    },
    handleAssigneeSelected(value) {
      this.$set(this.checklist, "assignee", value);
      this.$set(this.checklist, "assignee_email", value.username);
      this.$emit("update:checklist", {
        checklist: this.checklist,
        index: this.checklistIndex,
      });
    },
    newSubtask(ind = 0) {
      if (this.subtaskText.length === 0) {
        return;
      }

      this.subtaskText.forEach((el, index) => {
        let template = this.subtaskTemplate(el);
        this.checklist.subtasks.push(template);
      });

      this.$emit("update:checklist", {
        checklist: this.checklist,
        index: this.checklistIndex,
      });
      this.subtaskText = [];
    },
    handleNewBoat(boat) {
      if (Object.keys(boat).length === 0) {
        this.checklist.crm_boat_uuid = null;
        this.checklist.crm_boat = null;
      } else {
        this.checklist.crm_boat_uuid = boat.uuid;
      }
      this.$emit("update:checklist", {
        checklist: this.checklist,
        index: this.checklistIndex,
      });
    },
  },
};
</script>

<style></style>
